import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import MDModal from "components/MDModal";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import AddMemberForm from "./Form/member";
import { memberList } from "Apis/member";
import DataTable from "examples/Tables/DataTable";
import DefaultCell from "examples/Tables/DataTable/components/DefaultCell";
import PropTypes from "prop-types";
import MDBox from "components/MDBox";
import { deleteMember } from "Apis/member";
import { resetMemberPhoneNumberViewLimit } from "Apis/member";
import { ModalContext } from "context/ModalContext";
import { LoginFormContext } from "context/LoginFormContext";
import { getMember, verifyMember } from "Apis/member";
import { MODULES } from "helpers/constant";
import UpdateStatus from "components/MDUpdateStatus";
import { Avatar, Tooltip } from "@mui/material";
import { CommonContext } from "context/CommonContext";
import { MemberContext } from "context/MemberContext";
import VerifiedIcon from "@mui/icons-material/Verified";
import MDActionButton from "components/MDActionButton";
import colors from "assets/theme/base/colors";
import VerifiedStatus from "components/MDVerifiedStatus";
import Imagepreview from "components/MDImagePreview";
import { SEARCH_INPUT_TYPES } from "helpers/constant";
import SubmitButton from "components/SubmitButton";
import moment from "moment";
import { createColumnHelper } from "@tanstack/react-table";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { statusData } from "helpers/constant";
import CollapseTable from "examples/Tables/CollapseTable";
import AddCircleIcon from "@mui/icons-material/AddCircle";

const columnHelper = createColumnHelper();

const Members = () => {
  const { showMessageNotification, setOpen, formIsEdit, setFormIsEdit } = useContext(ModalContext);

  const {
    setNotification,
    memberEditData,
    setMemberEditData,
    modules,
    getModuleAccess,
    access,
    isLoading,
    setIsLoading,
    filterLoader,
    setFilterLoader,
  } = useContext(LoginFormContext);

  const { districtList, talukaList, districts, talukas, villageList } = useContext(CommonContext);

  const { parentDataListing, educationListing, occupationListing, setCurrentSelectedMember } =
    useContext(MemberContext);

  const [member, setMember] = useState([]);
  const { imageBorder } = colors;

  const [rowCount, setRowCount] = useState(0);

  const [defaultFilter, setDefaultFilter] = useState({
    currentPage: 1,
    itemsPerPage: 10,
    filters: {},
    sortBy: {
      id: "",
      value: "",
    },
  });

  /**
   * ---------------------------------------------------------
   * API DEBOUNCE + LATEST REQUEST HANDLING
   * ---------------------------------------------------------
   */

  const debounceRef = useRef(null);
  const requestIdRef = useRef(0);

  /**
   * Fetch member listing.
   *
   * - useCallback keeps the function reference stable.
   * - Debounce prevents API call on every filter/search change.
   * - requestIdRef ensures an old/slower API response
   *   cannot overwrite a newer API response.
   */
  const memberListing = useCallback(
    async (data = defaultFilter) => {
      // Cancel previously scheduled debounce
      clearTimeout(debounceRef.current);

      /**
       * Increment request ID immediately.
       *
       * This invalidates any currently running API request.
       * If an older API request finishes later, its response
       * will be ignored.
       */
      const requestId = ++requestIdRef.current;

      debounceRef.current = setTimeout(async () => {
        try {
          // setIsLoading(true);

          const response = await memberList(data);

          /**
           * Ignore response if another request was started
           * after this request.
           */
          if (requestId !== requestIdRef.current) {
            return;
          }

          if (response?.status === 200) {
            setMember(response?.data?.data?.rows || []);
            setRowCount(response?.data?.data?.count || 0);
          }
        } catch (error) {
          /**
           * Ignore error from old request.
           */
          if (requestId !== requestIdRef.current) {
            return;
          }

          console.error("Member listing error:", error);
        } finally {
          /**
           * Only the latest request is allowed to change
           * the loading state.
           */
          if (requestId === requestIdRef.current) {
            setIsLoading(false);
          }
        }
      }, 500);
    },
    [defaultFilter, setIsLoading]
  );

  /**
   * ---------------------------------------------------------
   * FILTER STATE
   * ---------------------------------------------------------
   */

  const [filterVal, setFilterVal] = useState({
    first_name: "",
  });

  const handleFilter = (accessor, value) => {
    setFilterLoader(true);

    setFilterVal((prev) => ({
      ...prev,
      [accessor]: value,
    }));
  };

  /**
   * ---------------------------------------------------------
   * FORM
   * ---------------------------------------------------------
   */

  const handleOpenForm = async (id) => {
    setFormIsEdit(false);
    setCurrentSelectedMember(id);
    setOpen(true);
  };

  /**
   * ---------------------------------------------------------
   * TABLE COLUMNS
   * ---------------------------------------------------------
   */

  const defaultColumns = [
    columnHelper.accessor("-", {
      cell: (info) => (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            paddingLeft: `${info?.row?.depth * 1.5}rem`,
          }}
        >
          {info?.row?.getCanExpand() && (
            <>
              {info?.row?.getIsExpanded() ? (
                <KeyboardArrowUpIcon
                  onClick={info.row.getToggleExpandedHandler()}
                  style={{ cursor: "pointer" }}
                />
              ) : (
                <KeyboardArrowDownIcon
                  onClick={info.row.getToggleExpandedHandler()}
                  style={{ cursor: "pointer" }}
                />
              )}
            </>
          )}
        </div>
      ),
    }),

    columnHelper.accessor("member_number", {
      header: "Member Number",
      accessor: "member_number",
      filterLabel: "Member Number",
      filterAccessor: "member_number",
      filterInputType: SEARCH_INPUT_TYPES.TEXT,
      sort: true,
      cell: (info) => {
        const value = info.getValue();

        return (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              paddingLeft: `${info?.row?.depth * 1.5}rem`,
              minWidth: "180px",
            }}
          >
            <span
              style={{
                paddingLeft: "10px",
                cursor: "pointer",
              }}
              onClick={() => handleEditDelete(info.row.original, "Edit")}
            >
              {value || "-"}
            </span>
          </div>
        );
      },
    }),

    columnHelper.accessor("first_name", {
      id: "first_name",
      accessor: "first_name",
      minWidth: "85px",
      sort: true,
      filterLabel: "First Name",
      filterAccessor: "first_name",
      filterInputType: SEARCH_INPUT_TYPES.TEXT,
      header: "First Name",

      cell: (info) => {
        const imageUrl = info?.row?.original?.member_images?.[0]?.image;

        const [imgError, setImgError] = React.useState(false);

        const isVerified = info?.row?.original?.is_verified === 1;

        const value = info.getValue();

        return (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "10px",
              paddingLeft: `${info?.row?.depth * 1.5}rem`,
            }}
          >
            {imageUrl && !imgError ? (
              <div
                style={{
                  position: "relative",
                  display: "inline-block",
                }}
              >
                <img
                  src={imageUrl}
                  alt="image"
                  onError={() => setImgError(true)}
                  onClick={() => handleImageClick(imageUrl)}
                  style={{
                    height: "50px",
                    width: "50px",
                    borderRadius: "100%",
                    objectFit: "cover",
                    border: `1px solid ${imageBorder.borderColor}`,
                    cursor: "pointer",
                  }}
                />

                {isVerified && (
                  <VerifiedIcon
                    sx={{
                      position: "absolute",
                      bottom: "5px",
                      right: 0,
                      backgroundColor: "#fff",
                      borderRadius: "50%",
                      width: "16px",
                      height: "16px",
                      color: `${imageBorder.verifiedIconColor}`,
                    }}
                  />
                )}
              </div>
            ) : (
              <div
                style={{
                  position: "relative",
                  display: "inline-block",
                }}
              >
                <Avatar
                  sx={{
                    height: "50px",
                    width: "50px",
                    "&:hover": {
                      background: `${imageBorder.backgroundColor} !important`,
                    },
                  }}
                />

                {isVerified && (
                  <VerifiedIcon
                    sx={{
                      position: "absolute",
                      bottom: 0,
                      right: 0,
                      backgroundColor: "#fff",
                      borderRadius: "50%",
                      width: "16px",
                      height: "16px",
                      color: `${imageBorder.verifiedIconColor}`,
                    }}
                  />
                )}
              </div>
            )}

            <DefaultCell value={value || "-"} />
          </div>
        );
      },
    }),

    columnHelper.accessor("middle_name", {
      header: "Middle Name",
      accessor: "middle_name",
      minWidth: "85px",
      filterLabel: "Middle Name",
      filterAccessor: "middle_name",
      filterInputType: SEARCH_INPUT_TYPES.TEXT,
      sort: true,

      cell: (info) => {
        const value = info.getValue();

        return (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              paddingLeft: `${info?.row?.depth * 1.5}rem`,
              minWidth: "180px",
            }}
          >
            <span style={{ paddingLeft: "10px" }}>{value || "-"}</span>
          </div>
        );
      },
    }),

    columnHelper.accessor("surname", {
      header: "Surname",
      accessor: "surname",
      filterLabel: "Surname",
      filterAccessor: "surname",
      filterInputType: SEARCH_INPUT_TYPES.TEXT,
      sort: true,

      cell: (info) => {
        const value = info.getValue();

        return (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              paddingLeft: `${info?.row?.depth * 1.5}rem`,
              minWidth: "180px",
            }}
          >
            <span style={{ paddingLeft: "10px" }}>{value || "-"}</span>
          </div>
        );
      },
    }),

    columnHelper.accessor("mobile_number", {
      header: "Mobile Number",
      accessor: "mobile_number",
      filterLabel: "Mobile Number",
      filterAccessor: "mobile_number",
      filterInputType: SEARCH_INPUT_TYPES.TEXT,
      sort: true,

      cell: (info) => {
        const value = info.getValue();

        return (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              paddingLeft: `${info?.row?.depth * 1.5}rem`,
              minWidth: "180px",
            }}
          >
            <span style={{ paddingLeft: "10px" }}>{value || "-"}</span>
          </div>
        );
      },
    }),

    columnHelper.accessor("gender", {
      header: "Gender",
      accessor: "gender",
      filterLabel: "Gender",
      filterAccessor: "gender",
      filterInputType: SEARCH_INPUT_TYPES.AUTO_COMPLETE,

      options: [
        {
          label: "Male",
          value: "Male",
        },
        {
          label: "Female",
          value: "Female",
        },
      ],

      sort: true,

      cell: (info) => {
        const value = info.getValue();

        return (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              paddingLeft: `${info?.row?.depth * 1.5}rem`,
              minWidth: "200px",
            }}
          >
            <span style={{ paddingLeft: "10px" }}>{value || "-"}</span>
          </div>
        );
      },
    }),

    columnHelper.accessor("is_head_person", {
      header: "Head Member",
      accessor: "is_head_person",
      filterLabel: "Head Member",
      filterAccessor: "is_head_person",
      filterInputType: SEARCH_INPUT_TYPES.AUTO_COMPLETE,

      options: [
        {
          label: "Head",
          value: "1",
        },
        {
          label: "Not Head",
          value: "0",
        },
      ],

      sort: true,

      cell: (info) => {
        return (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              paddingLeft: `${info?.row?.depth * 1.5}rem`,
              minWidth: "200px",
            }}
          >
            <SubmitButton
              background={info.getValue() === 1 ? "#a68181" : "#7f868f"}
              hoverBackground={info.getValue() === 1 ? "#a68181" : "#7f868f"}
              color="#fff"
              btnText={info.getValue() === 1 ? "Head" : "Not Head"}
              type="button"
            >
              {info.getValue() === 1 ? "Head" : "Not Head"}
            </SubmitButton>
          </div>
        );
      },
    }),

    columnHelper.accessor("is_verified", {
      header: "Verification Status",
      accessor: "is_verified",
      filterLabel: "Verification Status",
      filterAccessor: "is_verified",
      filterInputType: SEARCH_INPUT_TYPES.AUTO_COMPLETE,

      options: [
        {
          label: "Verified",
          value: "1",
        },
        {
          label: "Not Verified",
          value: "0",
        },
      ],

      sort: true,

      cell: (info) => {
        return (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              paddingLeft: `${info?.row?.depth * 1.5}rem`,
              minWidth: "200px",
            }}
          >
            <VerifiedStatus data={info.getValue()} />
          </div>
        );
      },
    }),

    columnHelper.accessor("member_verified_by", {
      header: "Verify By",
      accessor: "member_verified_by",

      cell: (info) => {
        const value = info.getValue();

        return (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              paddingLeft: `${info?.row?.depth * 1.5}rem`,
              minWidth: "180px",
            }}
          >
            <span style={{ paddingLeft: "10px" }}>
              {value?.first_name && value?.last_name
                ? `${value.first_name} ${value.last_name}`
                : "-"}
            </span>
          </div>
        );
      },
    }),

    columnHelper.accessor("createdAt", {
      header: "Created date",
      accessor: "createdAt",
      filterAccessor: "createdAt",
      sort: true,

      cell: (info) => {
        const createdAtValue = info.getValue();

        const formattedDate = createdAtValue
          ? moment(createdAtValue).format("DD-MM-YYYY hh:mm A")
          : "-";

        return (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              paddingLeft: `${info?.row?.depth * 1.5}rem`,
              minWidth: "200px",
            }}
          >
            <span>{formattedDate || "-"}</span>
          </div>
        );
      },
    }),

    columnHelper.accessor("is_active", {
      id: "is_active",
      accessor: "is_active",
      filterAccessor: "is_active",
      sort: false,
      filterLabel: "Status",
      filterInputType: SEARCH_INPUT_TYPES.AUTO_COMPLETE,
      header: "Status",
      options: statusData,

      cell: (info) => (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            paddingLeft: `${info?.row?.depth * 1.5}rem`,
            width: "200px",
          }}
        >
          <UpdateStatus
            tableNameProp="members"
            data={info?.row}
            access={access?.write_access ? true : false}
          />
        </div>
      ),
    }),

    columnHelper.accessor("Action", {
      id: "Action",
      header: "Action",

      cell: (info) => (
        <MDBox
          sx={{
            display: "flex",
            gap: "10px",
            alignItems: "center",
          }}
        >
          {access?.write_access && (
            <MDActionButton
              type="edit"
              onClick={() => handleEditDelete(info.row.original, "Edit")}
            />
          )}

          {access?.delete_access && (
            <MDActionButton
              type="delete"
              onClick={() => handleEditDelete(info.row.original, "Delete")}
            />
          )}

          {access?.write_access && !info.row.depth && (
            <MDActionButton type="add" onClick={() => handleOpenForm(info?.row?.original?.id)} />
          )}

          {access?.write_access && !info.row.depth && (
            <MDActionButton
              type="reset"
              onClick={() => handleEditDelete(info.row.original, "Reset")}
            />
          )}

          {access?.write_access && !info.row.original.is_verified && (
            <MDActionButton
              type="verify"
              onClick={() => handleEditDelete(info.row.original, "Verify")}
            />
          )}
        </MDBox>
      ),
    }),
  ];

  /**
   * ---------------------------------------------------------
   * EDIT / DELETE / VERIFY / RESET
   * ---------------------------------------------------------
   */

  const handleEditDelete = async (row, type) => {
    /**
     * EDIT
     */
    if (type === "Edit") {
      setIsLoading(true);

      await parentDataListing();

      await districtList();
      await talukaList();
      await villageList();

      await educationListing();
      await occupationListing();

      const response = await getMember(row.id);

      if (response?.status === 200) {
        setMemberEditData(response?.data?.data);
        setOpen(true);
        setFormIsEdit(true);
      }

      setFilterLoader(false);
      setIsLoading(false);
    }

    /**
     * DELETE
     */
    if (type === "Delete") {
      showMessageNotification({
        title: "Are you sure?",
        text: "You want to delete this member!",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Yes, delete it!",
      }).then(async (result) => {
        if (result.isConfirmed) {
          const deleteResponse = await deleteMember(row.id);

          if (deleteResponse?.status === 200) {
            setFilterLoader(true);
            setOpen(false);

            if (member?.length === 1) {
              setDefaultFilter((prev) => ({
                ...prev,
                currentPage: 1,
              }));
            } else {
              /**
               * Refresh current listing.
               *
               * Do not depend on state update here.
               */
              memberListing(defaultFilter);
            }

            setNotification({
              color: "success",
              icon: "check",
              title: "Success",
              content: deleteResponse.data.message,
              open: true,
            });
          } else {
            setNotification({
              color: "error",
              icon: "check",
              title: "error",
              content: deleteResponse?.data?.message,
              open: true,
            });
          }
        }
      });
    }

    /**
     * VERIFY
     */
    if (type === "Verify") {
      showMessageNotification({
        title: "Are you sure?",
        text: "You want to verify this member!",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Yes, verify it!",
      }).then(async (result) => {
        if (result.isConfirmed) {
          const response = await getMember(row?.id);

          if (response?.status === 200) {
            const data = response?.data?.data;

            data.is_verified = 1;

            const verifyResponse = await verifyMember(row?.id);

            if (verifyResponse?.status === 200) {
              setFilterLoader(true);
              setOpen(false);

              if (member?.length === 1) {
                setDefaultFilter((prev) => ({
                  ...prev,
                  currentPage: 1,
                }));
              } else {
                memberListing(defaultFilter);
              }

              setNotification({
                color: "success",
                icon: "check",
                title: "Success",
                content: verifyResponse.data.message,
                open: true,
              });
            } else {
              setNotification({
                color: "error",
                icon: "check",
                title: "error",
                content: verifyResponse?.data?.message,
                open: true,
              });
            }
          }
        }
      });
    }

    /**
     * RESET PHONE NUMBER VIEW LIMIT
     */
    if (type === "Reset") {
      showMessageNotification({
        title: "Are you sure?",
        text: "You want to reset this member's phone number view limit!",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Yes, reset it!",
      }).then(async (result) => {
        if (result.isConfirmed) {
          const resetResponse = await resetMemberPhoneNumberViewLimit(row.id);

          if (resetResponse?.status === 200) {
            setFilterLoader(true);
            setOpen(false);

            if (member?.length === 1) {
              setDefaultFilter((prev) => ({
                ...prev,
                currentPage: 1,
              }));
            } else {
              memberListing(defaultFilter);
            }

            setNotification({
              color: "success",
              icon: "check",
              title: "Success",
              content: resetResponse.data.message,
              open: true,
            });
          }
        }
      });
    }
  };

  /**
   * ---------------------------------------------------------
   * MODULE ACCESS
   * ---------------------------------------------------------
   */

  useEffect(() => {
    getModuleAccess(MODULES.MEMBER);
  }, [modules]);

  /**
   * ---------------------------------------------------------
   * MEMBER LISTING
   * ---------------------------------------------------------
   *
   * defaultFilter is the source of truth for:
   * - pagination
   * - filters
   * - sorting
   *
   * Therefore we don't need filterVal here.
   */
  useEffect(() => {
    memberListing(defaultFilter);
  }, [defaultFilter, memberListing]);

  /**
   * ---------------------------------------------------------
   * PARENT DATA
   * ---------------------------------------------------------
   */

  // useEffect(() => {
  //   parentDataListing();
  // }, []);

  /**
   * ---------------------------------------------------------
   * CLEANUP
   * ---------------------------------------------------------
   */

  useEffect(() => {
    return () => {
      /**
       * Cancel pending debounce.
       */
      clearTimeout(debounceRef.current);

      /**
       * Invalidate any running API request.
       *
       * If the API resolves after component unmount,
       * its response will be ignored.
       */
      requestIdRef.current += 1;
    };
  }, []);

  /**
   * ---------------------------------------------------------
   * IMAGE PREVIEW
   * ---------------------------------------------------------
   */

  const [selectedImage, setSelectedImage] = useState(null);
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);

  const handleImageClick = (imageUrl) => {
    setSelectedImage(imageUrl);
    setIsImageModalOpen(true);
  };

  /**
   * ---------------------------------------------------------
   * RENDER
   * ---------------------------------------------------------
   */

  return (
    !isLoading && (
      <>
        <DashboardLayout>
          <DashboardNavbar />

          <>
            <MDModal
              hasButton={access?.write_access ?? false}
              btnText="add member"
              modalHead={`${formIsEdit ? "Edit" : "Add"} Member`}
            >
              <AddMemberForm
                memberListing={memberListing}
                nativeDistricts={districts}
                nativeTalukas={talukas}
              />
            </MDModal>

            <CollapseTable
              tableTitle="Member List"
              data={member}
              defaultColumns={defaultColumns}
              totalEntries={rowCount}
              defaultFilter={defaultFilter}
              setDefaultFilter={setDefaultFilter}
              canFilter
              subrow={["members"]}
            />

            {selectedImage && (
              <Imagepreview
                open={isImageModalOpen}
                onClose={() => setIsImageModalOpen(false)}
                imageUrl={selectedImage}
              />
            )}
          </>
        </DashboardLayout>
      </>
    )
  );
};

Members.propTypes = {
  value: PropTypes.string,
  row: PropTypes.string,
  memberListing: PropTypes.func,
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  imageUrl: PropTypes.string.isRequired,
};

export default Members;
